import { useEffect } from "react";

/**
 * Global scroll-reveal — mirrors initReveal() from the design's site.js.
 *
 * The shared design system sets `.reveal { opacity: 0 }` and only reveals an
 * element once it also has `.in`. This hook runs a single IntersectionObserver
 * that adds `.in` to every `.reveal` node, then stops watching it.
 *
 * It also watches the DOM with a MutationObserver so nodes rendered *after*
 * the initial scan — e.g. lists loaded via axios, or a new page mounted on
 * navigation — are still picked up and revealed.
 *
 * Re-runs whenever `key` changes (pass the current route) so the observers are
 * rebound against the freshly-mounted page.
 *
 * @param {*} key - value that, when changed, rebinds the observers (e.g. pathname)
 */
export default function useReveal(key) {
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Respect reduced-motion: reveal everything at once, no observers.
    if (prefersReduced) {
      document
        .querySelectorAll(".reveal:not(.in)")
        .forEach((el) => el.classList.add("in"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      // threshold 0 = reveal as soon as any part enters the viewport. Using a
      // fractional threshold hides tall elements (e.g. the contact form card)
      // that are only partially visible on load — their visible strip never
      // reaches the fraction, so `.in` is never added until the user scrolls.
      { threshold: 0 }
    );

    const observeAll = (root = document) =>
      root
        .querySelectorAll(".reveal:not(.in)")
        .forEach((el) => io.observe(el));

    observeAll();

    // Pick up `.reveal` nodes added after the initial scan (async content).
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return; // elements only
          if (node.matches?.(".reveal:not(.in)")) io.observe(node);
          if (node.querySelectorAll) observeAll(node);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [key]);
}
